import React, { useCallback, useEffect, useRef, useState } from "react";
import { App as AntdApp, Checkbox, Form, Input, Select, Space, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import {
  CloudDownloadOutlined,
  CloudOutlined,
  CloudServerOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  UndoOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Card, Modal, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import axiosClient from "../../api/axiosClient";
import { handleAxiosError } from "../../components/Common/apiHelpers";
import ScheduleCard from "./components/ScheduleCard";
import RestoreModal from "./components/RestoreModal";
import UploadRestoreModal from "./components/UploadRestoreModal";
import ExternalRestorePanel from "./components/ExternalRestorePanel";
import BackupFilesPanel from "./components/BackupFilesPanel";
import FtpConnectionsPanel from "./components/FtpConnectionsPanel";
import StoragePanel from "./components/StoragePanel";
import {
  BACKUPS_ENDPOINT,
  BACKUP_STATUS,
  BACKUP_STATUS_COLOR,
  BACKUP_STATUS_LABEL,
  BACKUP_TYPE_COLOR,
  BACKUP_TYPE_LABEL,
  FTP_ENDPOINT,
  FTP_UPLOAD_STATUS,
  FTP_UPLOAD_STATUS_COLOR,
  FTP_UPLOAD_STATUS_LABEL,
  STORAGE_ENDPOINT,
  STORAGE_UPLOAD_STATUS,
  STORAGE_UPLOAD_STATUS_COLOR,
  STORAGE_UPLOAD_STATUS_LABEL,
  formatBytes,
  formatDuration,
} from "./constants";

const { Text } = Typography;

const BackupsPage = () => {
  const { mutate } = useMutations();
  // useMutations returns a fresh `mutate` on every render and that function sets state,
  // so depending on its identity inside an effect would re-fetch forever. The ref keeps
  // the callbacks below stable — same pattern as CostMarginReport and PermissionsDrawer.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const { confirmAction } = useConfirm();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [localTables, setLocalTables] = useState([]);
  const [restoreId, setRestoreId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [ftpConnections, setFtpConnections] = useState([]);
  const [storage, setStorage] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [storing, setStoring] = useState(null);
  // Bumped whenever the FTP or S3 tab changes something, so the schedule card and the create
  // modal re-read the connections without a page reload.
  const [ftpVersion, setFtpVersion] = useState(0);

  const sendToFtp = Form.useWatch("sendToFtp", form);
  const storageReady = !!storage?.isConfigured;

  const load = useCallback(async () => {
    setLoading(true);
    await mutateRef.current("listBackups", {
      method: "get",
      endpoint: BACKUPS_ENDPOINT,
      params: { take: 100 },
      onSuccess: (data) => setBackups(data || []),
    });
    setLoading(false);
  }, []);

  const loadFtpConnections = useCallback(async () => {
    await mutateRef.current("backupFtpConnections", {
      method: "get",
      endpoint: FTP_ENDPOINT,
      onSuccess: (data) => setFtpConnections((data || []).filter((c) => c.isActive)),
    });
  }, []);

  const loadStorage = useCallback(async () => {
    await mutateRef.current("backupStorageSettings", {
      method: "get",
      endpoint: STORAGE_ENDPOINT,
      onSuccess: (data) => setStorage(data || null),
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadFtpConnections();
    loadStorage();
  }, [loadFtpConnections, loadStorage, ftpVersion]);

  const openCreate = async () => {
    form.resetFields();
    setCreateOpen(true);

    if (localTables.length === 0) {
      await mutate("backupTables", {
        method: "get",
        endpoint: `${BACKUPS_ENDPOINT}/tables`,
        onSuccess: (data) => setLocalTables(data || []),
      });
    }
  };

  const createBackup = async () => {
    const values = await form.validateFields();
    setCreating(true);

    await mutate("createBackup", {
      method: "post",
      endpoint: BACKUPS_ENDPOINT,
      payload: {
        note: values.note || null,
        tables: values.tables?.length ? values.tables : null,
        sendToFtp: !!values.sendToFtp,
        // Null lets the server fall back to whatever the schedule uploads to.
        ftpConnectionId: values.sendToFtp ? values.ftpConnectionId ?? null : null,
        sendToStorage: !!values.sendToStorage,
      },
      onSuccess: () => {
        setCreateOpen(false);
        load();
      },
    });

    setCreating(false);
  };

  /** Retry — or a first attempt — at putting an existing archive on the FTP target. */
  const uploadToFtp = async (record) => {
    setUploading(record.backupId);

    await mutate("uploadBackupToFtp", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/${record.backupId}/ftp`,
      payload: { ftpConnectionId: null },
      onSuccess: load,
    });

    setUploading(null);
  };

  /** Retry — or a first attempt — at putting an existing archive in the bucket. */
  const uploadToStorage = async (record) => {
    setStoring(record.backupId);

    await mutate("uploadBackupToStorage", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/${record.backupId}/storage`,
      onSuccess: load,
    });

    setStoring(null);
  };

  // The archive is a file download rather than a JSON payload, so it bypasses the
  // shared mutate helper and goes straight through the axios client.
  const download = async (record) => {
    setDownloading(record.backupId);

    try {
      const response = await axiosClient.get(`${BACKUPS_ENDPOINT}/${record.backupId}/download`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = record.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleAxiosError(error, "Could not download the archive.");
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    {
      title: "Archive",
      dataIndex: "fileName",
      key: "fileName",
      // Every other column is fixed, so without a width of its own this one is handed
      // whatever is left of the scroll width — which is a few characters per line once
      // the FTP and S3 columns are in. The file name is the row's identity: it gets the
      // room to stay on one line, and only the note underneath wraps.
      width: 300,
      render: (fileName, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ whiteSpace: "nowrap" }}>
            {fileName}
          </Text>
          {record.note && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.note}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 110,
      render: (type) => <Tag color={BACKUP_TYPE_COLOR[type]}>{BACKUP_TYPE_LABEL[type]}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) =>
        record.error ? (
          <Tooltip title={record.error}>
            <Tag color={BACKUP_STATUS_COLOR[status]}>{BACKUP_STATUS_LABEL[status]}</Tag>
          </Tooltip>
        ) : (
          <Tag color={BACKUP_STATUS_COLOR[status]}>{BACKUP_STATUS_LABEL[status]}</Tag>
        ),
    },
    {
      title: "Contents",
      key: "contents",
      width: 170,
      render: (_, record) => (
        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {record.tableCount} tables · {(record.rowCount ?? 0).toLocaleString()} rows
        </Text>
      ),
    },
    {
      title: "Size",
      dataIndex: "sizeBytes",
      key: "sizeBytes",
      width: 100,
      align: "right",
      render: (bytes, record) =>
        record.fileExists ? (
          formatBytes(bytes)
        ) : (
          <Tooltip title="The file is no longer on disk.">
            <Text type="secondary">missing</Text>
          </Tooltip>
        ),
    },
    {
      title: "FTP",
      key: "ftp",
      width: 120,
      render: (_, record) => {
        if (record.ftpStatus === null || record.ftpStatus === undefined)
          return <Text type="secondary">—</Text>;

        const tooltip =
          record.ftpStatus === FTP_UPLOAD_STATUS.UPLOADED
            ? `${record.ftpRemotePath} on ${record.ftpConnectionName || "the FTP target"}`
            : record.ftpError;

        return (
          <Tooltip title={tooltip}>
            <Tag color={FTP_UPLOAD_STATUS_COLOR[record.ftpStatus]}>
              {FTP_UPLOAD_STATUS_LABEL[record.ftpStatus]}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "S3",
      key: "storage",
      width: 120,
      render: (_, record) => {
        if (record.storageStatus === null || record.storageStatus === undefined)
          return <Text type="secondary">—</Text>;

        const tooltip =
          record.storageStatus === STORAGE_UPLOAD_STATUS.UPLOADED
            ? `${record.storageKey} in ${storage?.bucket || "the bucket"}`
            : record.storageError;

        return (
          <Tooltip title={tooltip}>
            <Tag color={STORAGE_UPLOAD_STATUS_COLOR[record.storageStatus]}>
              {STORAGE_UPLOAD_STATUS_LABEL[record.storageStatus]}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Taken",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      render: (date, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ whiteSpace: "nowrap" }}>{new Date(date).toLocaleString()}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatDuration(record.durationMs)}
            {record.createdByUsername ? ` · ${record.createdByUsername}` : ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 230,
      // Pinned so the row's controls are reachable without scrolling the table to its end.
      fixed: "right",
      render: (_, record) => {
        const usable = record.status === BACKUP_STATUS.COMPLETED && record.fileExists;
        const ftpTooltip = !ftpConnections.length
          ? "No FTP connection is configured"
          : !usable
            ? "Only a completed archive that is still on disk can be uploaded"
            : record.ftpStatus === FTP_UPLOAD_STATUS.UPLOADED
              ? "Upload again"
              : "Send to FTP";

        const storageTooltip = !storageReady
          ? "The S3 connection is not set up"
          : !usable
            ? "Only a completed archive that is still on disk can be uploaded"
            : record.storageStatus === STORAGE_UPLOAD_STATUS.UPLOADED
              ? "Copy again"
              : "Copy to S3";

        return (
          <Space size={0}>
            <Tooltip title={usable ? "Restore" : "This archive cannot be restored"}>
              <Button
                variant="ghost"
                icon={<UndoOutlined />}
                disabled={!usable}
                onClick={() => setRestoreId(record.backupId)}
              />
            </Tooltip>

            <Tooltip title={ftpTooltip}>
              <Button
                variant="ghost"
                icon={<CloudUploadOutlined />}
                disabled={!usable || !ftpConnections.length}
                loading={uploading === record.backupId}
                onClick={() => uploadToFtp(record)}
              />
            </Tooltip>

            <Tooltip title={storageTooltip}>
              <Button
                variant="ghost"
                icon={<CloudOutlined />}
                disabled={!usable || !storageReady}
                loading={storing === record.backupId}
                onClick={() => uploadToStorage(record)}
              />
            </Tooltip>

            <Tooltip title="Download">
              <Button
                variant="ghost"
                icon={<DownloadOutlined />}
                disabled={!record.fileExists}
                loading={downloading === record.backupId}
                onClick={() => download(record)}
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Button
                variant="ghost"
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  confirmAction({
                    key: "deleteBackup",
                    method: "delete",
                    endpoint: `${BACKUPS_ENDPOINT}/${record.backupId}`,
                    title: "Delete this backup?",
                    content: "The archive file is removed from disk as well. This cannot be undone.",
                    okText: "Delete",
                    okType: "danger",
                    onSuccess: load,
                  })
                }
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const archivesTab = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <ScheduleCard onSaved={() => message.success("Schedule saved.")} refreshKey={ftpVersion} />

      <Card
        title={
          <Space size={8}>
            <DatabaseOutlined />
            Archives
          </Space>
        }
        extra={
          <Space>
            <Button variant="secondary" icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
            <Button variant="secondary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              Restore from file
            </Button>
            <Button variant="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Back up now
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="backupId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={backups}
          pagination={{ pageSize: 15, size: "small", showSizeChanger: false }}
          // The sum of the column widths above. Anything smaller and antd squeezes the
          // columns instead of scrolling, which is what broke the archive names.
          scroll={{ x: 1460 }}
        />
      </Card>
    </Space>
  );

  return (
    <div>
      <PageHeader
        title="Backup & Restore"
        subtitle="Database archives, external restores and off-site copies to FTP or S3."
      />
      <Tabs
        defaultActiveKey="archives"
        items={[
          {
            key: "archives",
            label: (
              <Space size={6}>
                <CloudDownloadOutlined />
                Backups &amp; restore
              </Space>
            ),
            children: archivesTab,
          },
          {
            key: "files",
            label: (
              <Space size={6}>
                <FolderOpenOutlined />
                Backup files
              </Space>
            ),
            // A restore from here takes a safety backup, so the archive list has moved on.
            children: <BackupFilesPanel onRestored={load} />,
          },
          {
            key: "external",
            label: (
              <Space size={6}>
                <CloudServerOutlined />
                Restore from another server
              </Space>
            ),
            children: <ExternalRestorePanel onImported={load} />,
          },
          {
            key: "ftp",
            label: (
              <Space size={6}>
                <CloudUploadOutlined />
                FTP connection
              </Space>
            ),
            children: <FtpConnectionsPanel onChanged={() => setFtpVersion((v) => v + 1)} />,
          },
          {
            key: "storage",
            label: (
              <Space size={6}>
                <CloudOutlined />
                S3 storage
              </Space>
            ),
            children: <StoragePanel onChanged={() => setFtpVersion((v) => v + 1)} />,
          },
        ]}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Take a backup now"
        confirmText="Start backup"
        loading={creating}
        onConfirm={createBackup}
      >
        <Form form={form} layout="vertical" initialValues={{ sendToFtp: false, sendToStorage: false }}>
          <Form.Item
            name="note"
            label="Note"
            tooltip="Shown in the list — useful for marking why the backup was taken."
          >
            <Input placeholder="e.g. before the 2.4 rollout" maxLength={300} />
          </Form.Item>

          <Form.Item
            name="tables"
            label="Tables"
            tooltip="Leave empty to capture the whole database."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Whole database"
              options={localTables.map((t) => ({ value: t, label: t }))}
              maxTagCount="responsive"
            />
          </Form.Item>

          <Form.Item name="sendToFtp" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox disabled={!ftpConnections.length}>
              <Space size={6}>
                Send to FTP once the archive is written
                {!ftpConnections.length && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (no connection configured yet)
                  </Text>
                )}
              </Space>
            </Checkbox>
          </Form.Item>

          {sendToFtp && (
            <Form.Item
              name="ftpConnectionId"
              label="FTP connection"
              tooltip="Leave empty to use the one the automatic backup uploads to."
            >
              <Select
                allowClear
                placeholder="Same as the automatic backup"
                options={ftpConnections.map((c) => ({
                  value: c.ftpConnectionId,
                  label: `${c.name} — ${c.host}${c.remoteDirectory ? c.remoteDirectory : ""}`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item name="sendToStorage" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox disabled={!storageReady}>
              <Space size={6}>
                Copy to S3 once the archive is written
                {!storageReady && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (no connection configured yet)
                  </Text>
                )}
              </Space>
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <RestoreModal
        backupId={restoreId}
        open={restoreId !== null}
        onClose={() => setRestoreId(null)}
        onDone={load}
      />

      {/* A restore takes a safety backup, so the archive list has moved on afterwards. */}
      <UploadRestoreModal open={uploadOpen} onClose={() => setUploadOpen(false)} onDone={load} />

    </div>
  );
};

export default BackupsPage;
